import type { ReactElement } from 'react'
import {
  Briefcase,
  Network,
  Search,
  FileText,
  HelpCircle,
  Settings,
  BookOpen,
  Users,
  Server,
  type LucideIcon,
} from 'lucide-react'

export interface NavigationItem {
  label: string
  href: string
  icon?: string
  isActive?: boolean
}

interface MainNavProps {
  items: NavigationItem[]
  onNavigate?: ((href: string) => void) | undefined
}

const iconMap: Record<string, LucideIcon> = {
  cases: Briefcase,
  infrastructure: Network,
  analysis: Search,
  reports: FileText,
  reference: BookOpen,
  help: HelpCircle,
  settings: Settings,
  users: Users,
  server: Server,
}

function getIcon(iconName?: string): LucideIcon {
  if (!iconName) return Briefcase
  return iconMap[iconName.toLowerCase()] ?? Briefcase
}

export function MainNav({ items, onNavigate }: MainNavProps): ReactElement {
  const mainItems = items.filter(
    (item) => !['help', 'reference', 'settings'].includes(item.icon?.toLowerCase() ?? '')
  )
  const bottomItems = items.filter((item) =>
    ['help', 'reference', 'settings'].includes(item.icon?.toLowerCase() ?? '')
  )

  const renderItem = (item: NavigationItem): ReactElement => {
    const Icon = getIcon(item.icon)

    return (
      <button
        key={item.href}
        onClick={() => onNavigate?.(item.href)}
        className="mx-2 flex w-full items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition-colors duration-150"
        style={{
          fontFamily: 'var(--font-mono, IBM Plex Mono, monospace)',
          color: item.isActive ? 'var(--accent)' : 'var(--text-dim)',
          background: item.isActive ? 'var(--accent-dim)' : 'transparent',
        }}
        onMouseEnter={(e) => {
          if (!item.isActive) {
            e.currentTarget.style.background = 'var(--hover)'
            e.currentTarget.style.color = 'var(--text)'
          }
        }}
        onMouseLeave={(e) => {
          if (!item.isActive) {
            e.currentTarget.style.background = 'transparent'
            e.currentTarget.style.color = 'var(--text-dim)'
          }
        }}
      >
        <Icon className="h-5 w-5 flex-shrink-0" />
        <span>{item.label}</span>
      </button>
    )
  }

  return (
    <nav className="flex h-full flex-col">
      {/* Main navigation items */}
      <div className="flex-1 space-y-1">{mainItems.map((item) => renderItem(item))}</div>

      {/* Bottom navigation items */}
      {bottomItems.length > 0 && (
        <div className="mt-4 space-y-1 border-t pt-4" style={{ borderColor: 'var(--border)' }}>
          {bottomItems.map((item) => renderItem(item))}
        </div>
      )}
    </nav>
  )
}
